/**
 * Where a booking goes when nobody chooses (docs/auftrag-befunde-tutorials-vorratsbuch.md, A5): the
 * location with the most of it; without stock, in the pantry, where the article lay last, else the
 * storage its keeping asks for (dry → Vorratsschrank, cool → Kühlschrank, frozen → Gefriertruhe,
 * household goods → Haushaltsschrank), else a storage of its own profile. Drinks keep the cellar.
 */
import { baseProduct, suggestedKeep } from "./base-products.ts";
import { isPantry } from "./pantry.ts";
import type { Booking } from "./model.ts";

/** A location one may book in, with the article's stock there. */
export interface PlaceOption {
  id: string;
  lager: string;
  stock: number;
}

/** The starter storages of the pantry profiles (pantry.ts) by how a food is kept. */
const LAGER_FOR_KEEP: Record<string, string> = { trocken: "vorrat", kühl: "kuehl", tiefgekühlt: "truhe" };

/** The storage a pantry article belongs in by its fields: its keeping, else its base product's aisle. */
export function pantryLagerFor(f: Record<string, unknown>): string | undefined {
  if (f.profile === "haushalt") return "putzschrank";
  const keep = typeof f.keep === "string" && f.keep !== "" ? f.keep : suggestedKeep(baseProduct(f.base));
  return keep ? LAGER_FOR_KEEP[keep] : undefined;
}

export function defaultLocation(opts: {
  fields: Record<string, unknown>;
  /** In the order the app shows them. */
  places: readonly PlaceOption[];
  /** The profile of each storage (Lager), where it has one. */
  lagerProfile: (lager: string) => string | undefined;
  /** The storage's name, for the cellar of a drink. */
  lagerName: (lager: string) => string;
  /** Where the article was booked last (in, out or moved to), if anywhere. */
  last?: string | undefined;
}): string {
  const { fields, places } = opts;
  let best: PlaceOption | undefined;
  for (const p of places) if (p.stock > 0 && (!best || p.stock > best.stock)) best = p;
  if (best) return best.id;
  if (!isPantry(fields)) {
    // Nothing in stock yet: the cellar rather than the bar.
    return (places.find((p) => /keller/i.test(opts.lagerName(p.lager))) ?? places[0])?.id ?? "";
  }
  const last = places.find((p) => p.id === opts.last);
  if (last) return last.id;
  const wanted = pantryLagerFor(fields);
  const fits = wanted ? places.find((p) => p.lager === wanted) : undefined;
  if (fits) return fits.id;
  // A storage of the article's own profile before one of the other (the Haushaltsschrank is no place for flour).
  return (places.find((p) => opts.lagerProfile(p.lager) === fields.profile) ?? places[0])?.id ?? "";
}

/** Where an article was booked last in a journal: received, taken or moved to. */
export function lastPlace(journal: readonly Booking[], article: string): string | undefined {
  for (let i = journal.length - 1; i >= 0; i--) {
    const b = journal[i]!;
    if (b.kind === "move" && b.article === article) return b.to;
    if ((b.kind === "in" || b.kind === "out") && b.article === article) return b.location;
  }
  return undefined;
}

/**
 * Whether an article has a place of its own to go to (A5, the inbound): stock somewhere, a place
 * it lay last, or a storage its keeping asks for. Without one, the storage chosen for all rows.
 */
export function hasOwnPlace(fields: Record<string, unknown>, places: readonly PlaceOption[], last?: string): boolean {
  if (places.some((p) => p.stock > 0 || p.id === last)) return true;
  const wanted = pantryLagerFor(fields);
  return wanted !== undefined && places.some((p) => p.lager === wanted);
}

/** Whether „Einfrieren“ fits a food (C1): not what is kept dry (pasta, flour), except bread. */
export function freezable(f: Record<string, unknown>): boolean {
  const p = baseProduct(f.base);
  const keep = typeof f.keep === "string" && f.keep !== "" ? f.keep : suggestedKeep(p);
  return keep !== "trocken" || p?.aisle === "brot";
}

/** A freezer by its id or name: the starter „Gefriertruhe“ (`truhe`), or one named so („Tiefkühlschrank“). */
export const isFreezer = (lager: { id: string; name: string }) => lager.id === "truhe" || /gefrier|truhe|tiefk[üu]hl/i.test(lager.name);

/** Months a food keeps in the freezer, as suggested (C1): fruit, vegetables and bread 6, else 3. */
export function frozenMonths(f: Record<string, unknown>): number {
  const aisle = baseProduct(f.base)?.aisle;
  return aisle === "obst" || aisle === "brot" ? 6 : 3;
}

/**
 * The best-before a food gets when frozen (C1, Martin 26.10.2026): the day it is frozen plus months
 * by its aisle, fruit, vegetables and bread 6, everything else (meat, fish, what is cooked) 3. Only a
 * suggestion; the person may change it. Later from household-kit `food`.
 */
export function frozenUntil(f: Record<string, unknown>, frozen: string): string {
  const months = frozenMonths(f);
  const [y, m, d] = frozen.split("-").map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1 + months, d));
  // Short months: 31 August + 6 is the end of February, not the 3rd of March.
  if (at.getUTCDate() !== d) at.setUTCDate(0);
  return at.toISOString().slice(0, 10);
}
